import { api } from '@goaltree/backend/convex/_generated/api'
import { Button } from '@goaltree/ui/components/button'
import { useAction, useQuery } from 'convex/react'
import { ActivityIcon, Loader2, RefreshCwIcon, WatchIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { SettingsRow, rowId } from './primitives'

type Provider = 'fitbit' | 'strava'
const PROVIDERS: { id: Provider; icon: React.ReactNode }[] = [
  { id: 'fitbit', icon: <WatchIcon className="h-4 w-4" /> },
  { id: 'strava', icon: <ActivityIcon className="h-4 w-4" /> },
]

function ago(ms: number, lang: string) {
  const mins = Math.round((ms - Date.now()) / 60_000)
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' })
  return Math.abs(mins) < 60
    ? rtf.format(mins, 'minute')
    : rtf.format(Math.round(mins / 60), 'hour')
}

// Fitbit / Strava rows for Settings → Connected apps.
export function WearablesSettings() {
  const { t, i18n } = useTranslation()
  const available = useQuery(api.fitness.available)
  const conns = useQuery(api.fitness.myConnections)
  const getAuthUrl = useAction(api.fitness.getAuthUrl)
  const disconnect = useAction(api.fitness.disconnect)
  const syncNow = useAction(api.fitness.syncNow)
  const [busy, setBusy] = useState<string | null>(null)

  // Result of the OAuth round-trip (?fitness=connected|error), shown once.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const result = params.get('fitness')
    if (!result) return
    if (result === 'connected') toast.success(t('fitness.connectedToast'))
    else toast.error(t('fitness.errorToast'))
    params.delete('fitness')
    const q = params.toString()
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${q ? `?${q}` : ''}${window.location.hash}`,
    )
  }, [t])

  async function connect(provider: Provider) {
    setBusy(provider)
    try {
      window.location.href = await getAuthUrl({ provider, client: 'web' })
    } catch {
      toast.error(t('fitness.errorToast'))
      setBusy(null)
    }
  }

  async function remove(provider: Provider) {
    setBusy(provider)
    await disconnect({ provider }).catch(() => toast.error(t('common.error')))
    setBusy(null)
  }

  return (
    <>
      {PROVIDERS.map(({ id, icon }) => {
        const conn = conns?.find((c) => c.provider === id)
        const ready = !!available?.[id]
        const status = conn
          ? conn.status === 'reauth'
            ? t('fitness.reconnectNeeded')
            : conn.lastSyncAt
              ? t('fitness.syncedAgo', { time: ago(conn.lastSyncAt, i18n.language) })
              : t('fitness.connected')
          : ready
            ? t('fitness.notConnected')
            : t('fitness.comingSoon')
        return (
          <SettingsRow
            key={id}
            id={rowId(`fitness.${id}`)}
            icon={icon}
            label={t(`fitness.${id}`)}
            description={status}
          >
            {conn && conn.status === 'active' ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy === id}
                onClick={() => void remove(id)}
              >
                {busy === id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  t('fitness.disconnect')
                )}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                disabled={!ready || busy === id}
                onClick={() => void connect(id)}
              >
                {busy === id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : conn ? (
                  t('fitness.reconnect')
                ) : (
                  t('fitness.connect')
                )}
              </Button>
            )}
          </SettingsRow>
        )
      })}
      {!!conns?.length && (
        <SettingsRow
          id={rowId('fitness.syncNow')}
          icon={<RefreshCwIcon className="h-4 w-4" />}
          label={t('fitness.syncNow')}
        >
          <Button
            size="sm"
            variant="ghost"
            disabled={busy === 'sync'}
            onClick={() => {
              setBusy('sync')
              void syncNow({})
                .then(() => toast.success(t('fitness.synced')))
                .catch(() => toast.error(t('common.error')))
                .finally(() => setBusy(null))
            }}
          >
            {busy === 'sync' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              t('fitness.syncNow')
            )}
          </Button>
        </SettingsRow>
      )}
    </>
  )
}
